import { isReference } from '@apollo/client';
import type { FetchResult, StoreObject } from '@apollo/client';

import { isDevelopment } from '@constants/env';

import { useResponseHandler } from '@hooks/useResponseHandler';
import { useSession } from '@hooks/useSession';

import { RelatedPersonRecord } from '@models/related-person';

import { parseArgs, type CachedRecordList } from '@utils/apollo';

import {
  useDeleteRelatedPersonRecordMutation,
  useListRelatedPersonsQuery,
  usePostRelatedPersonRecordMutation,
  useUpdateRelatedPersonRecordMutation,
} from 'generated/related-person.generated';
import type {
  DeleteRelatedPersonRecordMutation,
  DeleteRelatedPersonRecordMutationVariables,
  PostRelatedPersonRecordMutation,
  PostRelatedPersonRecordMutationVariables,
  UpdateRelatedPersonRecordMutation,
  UpdateRelatedPersonRecordMutationVariables,
} from 'generated/related-person.generated';

export const useListRelatedPersons = (params?: { skip?: boolean }) => {
  const { handleError } = useResponseHandler();
  const {
    state: { activeProfile },
  } = useSession();
  const patient_id = activeProfile?.patient_id ?? '';
  const response = useListRelatedPersonsQuery({
    variables: { input: { patient: patient_id, count: 1500, page: 1 } },
    skip: !patient_id || params?.skip,
  });
  const { data, error } = response;
  handleError(error);
  const records = (data?.listRelatedPersons.data ?? []).map(
    (r) => new RelatedPersonRecord(r)
  );
  return { ...response, data: records };
};

export const useGetRelatedPerson = (params: { record_id: string }) => {
  const {
    state: { activeProfile },
  } = useSession();
  const patient_id = activeProfile?.patient_id ?? '';
  const response = useListRelatedPersonsQuery({
    variables: {
      input: {
        ...params,
        patient: patient_id,
        count: 1500,
        page: 1,
      },
    },
    skip: !patient_id,
  });
  const { data } = response;
  const record = data?.listRelatedPersons.data?.find(
    ({ id }) => id === params.record_id
  );
  return { ...response, data: record ? new RelatedPersonRecord(record) : null };
};

export function useCreateRelatedPerson(): [
  (
    params: PostRelatedPersonRecordMutationVariables
  ) => Promise<FetchResult<PostRelatedPersonRecordMutation>>,
  boolean,
] {
  const [mutate, { loading: baseLoading }] =
    usePostRelatedPersonRecordMutation();
  const { handleError, handleSuccess } = useResponseHandler({
    handlerType: 'snackbar',
    snackbarMsgId: 'error',
  });
  const loading = baseLoading;

  async function createRelatedPerson(
    params: PostRelatedPersonRecordMutationVariables
  ) {
    const response = await mutate({
      variables: { ...params },
      onError: (e) => handleError(e.graphQLErrors),
      onCompleted: () => handleSuccess(),
      update: (cache, { data: response }) => {
        if (!response?.postRelatedPersonRecord) {
          return;
        }
        const storeObject =
          response.postRelatedPersonRecord as unknown as StoreObject;
        try {
          cache.modify<{ listRelatedPersons: CachedRecordList }>({
            fields: {
              listRelatedPersons: (
                existing,
                { storeFieldName, toReference }
              ) => {
                if (isReference(existing)) {
                  return existing;
                }
                const parsed = parseArgs(storeFieldName);
                const paramsPatientId = params.input.patient.reference;
                if (parsed?.patient === paramsPatientId) {
                  return {
                    ...existing,
                    data: [...existing.data, toReference(storeObject)],
                  };
                }
                return existing;
              },
            },
          });
        } catch (e) {
          if (isDevelopment) {
            // eslint-disable-next-line no-console
            console.error(e);
          }
        }
      },
    });
    return response;
  }
  return [createRelatedPerson, loading];
}

export function useUpdateRelatedPerson(): [
  (
    params: UpdateRelatedPersonRecordMutationVariables
  ) => Promise<FetchResult<UpdateRelatedPersonRecordMutation>>,
  boolean,
] {
  const [mutate, { loading: baseLoading }] =
    useUpdateRelatedPersonRecordMutation();
  const { handleError, handleSuccess } = useResponseHandler({
    handlerType: 'snackbar',
    snackbarMsgId: 'error',
  });
  const loading = baseLoading;

  async function updateRelatedPerson(
    params: UpdateRelatedPersonRecordMutationVariables
  ) {
    const response = await mutate({
      variables: { ...params },
      onError: (e) => handleError(e.graphQLErrors),
      onCompleted: () => handleSuccess(),
    });
    return response;
  }
  return [updateRelatedPerson, loading];
}

export function useDeleteRelatedPerson(): [
  (
    params: DeleteRelatedPersonRecordMutationVariables['input']
  ) => Promise<FetchResult<DeleteRelatedPersonRecordMutation>>,
  boolean,
] {
  const [mutate, { loading: baseLoading }] =
    useDeleteRelatedPersonRecordMutation();
  const { handleError, handleSuccess } = useResponseHandler({
    handlerType: 'snackbar',
    snackbarMsgId: 'error',
  });
  const loading = baseLoading;

  async function deleteRelatedPerson(
    params: DeleteRelatedPersonRecordMutationVariables['input']
  ) {
    const response = await mutate({
      onError: (e) => handleError(e.graphQLErrors),
      onCompleted: () => handleSuccess(),
      variables: {
        input: {
          ...params,
        },
      },
      update: (cache, { data: response }) => {
        if (!response?.deleteRelatedPersonRecord) {
          return;
        }
        const { relatedPerson_id: id } = params;
        try {
          const normalizedId = cache.identify({
            id,
            __typename: 'RelatedPersonModel',
          });
          cache.evict({ id: normalizedId });
          cache.gc();
        } catch (e) {
          if (isDevelopment) {
            // eslint-disable-next-line no-console
            console.error(e);
          }
        }
      },
    });
    return response;
  }
  return [deleteRelatedPerson, loading];
}
